/**
 * A ~40-line external store with the same shape as a Zustand slice.
 *
 * Why not Zustand: the doctor portal has never had a client store, `zustand` is
 * not a dependency of this package, and this environment cannot run an install.
 * Rather than ship an import that doesn't resolve, this is the same contract
 * (`create(set => state)` -> a `useStore(selector)` hook, plus `.getState()` /
 * `.setState()` for imperative callers) built on React 18's own
 * `useSyncExternalStore`, with an optional localStorage `persist` mirror.
 *
 * It is a drop-in: swapping these two files for `zustand` + `zustand/middleware`
 * later is an import change in the two store modules and nothing else, because
 * no consumer touches anything this doesn't provide.
 *
 * Deliberately NOT here: middleware, devtools, transient subscriptions, shallow
 * comparison. Selectors must return a stable value (a primitive, or a value that
 * is reference-stable across unrelated updates) — `useSyncExternalStore` bails
 * out on `Object.is` equality, so a selector building a fresh object every call
 * would re-render forever. Every selector in this app returns a primitive or a
 * slice held by reference in state.
 */
import { useSyncExternalStore } from 'react';

type Listener = () => void;

export interface Store<T> {
  getState: () => T;
  setState: (patch: Partial<T> | ((prev: T) => Partial<T>)) => void;
  subscribe: (listener: Listener) => () => void;
}

export interface PersistOptions<T> {
  /** localStorage key. Omit `persist` entirely for in-memory-only state. */
  name: string;
  /** Which keys to write. Anything not listed stays session-local. */
  partialize: (state: T) => Partial<T>;
}

export function createStore<T extends object>(
  initial: T,
  persist?: PersistOptions<T>,
): Store<T> & { use: <S>(selector: (state: T) => S) => S } {
  let state: T = persist ? { ...initial, ...readPersisted<T>(persist.name) } : initial;
  const listeners = new Set<Listener>();

  const getState = () => state;

  const setState = (patch: Partial<T> | ((prev: T) => Partial<T>)) => {
    const next = typeof patch === 'function' ? patch(state) : patch;
    // Bail out when nothing actually changed, so an idempotent write (e.g. the
    // room re-registering the same active section on every render) doesn't wake
    // every subscriber.
    let changed = false;
    for (const key of Object.keys(next) as (keyof T)[]) {
      if (!Object.is(state[key], next[key])) { changed = true; break; }
    }
    if (!changed) return;

    state = { ...state, ...next };
    if (persist) writePersisted(persist.name, persist.partialize(state));
    listeners.forEach(l => l());
  };

  const subscribe = (listener: Listener) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  };

  const use = <S,>(selector: (s: T) => S): S =>
    useSyncExternalStore(subscribe, () => selector(state), () => selector(state));

  return { getState, setState, subscribe, use };
}

function readPersisted<T>(name: string): Partial<T> {
  // Private-mode, cleared site data and storage-blocked browsers all throw or
  // return junk here. A UI preference is never worth a white screen.
  try {
    const raw = localStorage.getItem(name);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Partial<T>) : {};
  } catch {
    return {};
  }
}

function writePersisted<T>(name: string, value: Partial<T>): void {
  try {
    localStorage.setItem(name, JSON.stringify(value));
  } catch {
    /* quota / blocked storage — the preference just doesn't survive a reload */
  }
}
